import {
  FETCH_LAYOUT_V2,
  SAVE_LAYOUT_V2,
  ADD_WIDGET_V2,
  UPDATE_WIDGET_POSITIONS_V2,
  UPDATE_WIDGET_CONFIG_V2,
  DELETE_WIDGET_V2,
  FETCH_WIDGET_DATA_V2,
  FETCH_BATCH_WIDGET_DATA_V2,
  SET_DASHBOARD_V2_LOADING,
  SET_DASHBOARD_V2_ERROR,
  LOGOUT
} from '../actions/types';
import { handleAction } from '../utils/handleAction';

const initialState = {
  loading: false,
  error: null,
  layout: null,
  widgetData: {}, // { widgetId: { data, loading, error, lastUpdated } }
  isLoading: false
};

const dashboardV2Reducer = (state = initialState, action) => {
  const { type, payload } = action;

  switch (type) {
    // ==================== Layout Management ====================
    
    case FETCH_LAYOUT_V2:
      return handleAction(state, action, {
        failure: (prevState) => ({
          ...prevState,
          error: payload?.error || 'Error al cargar el layout',
          loading: false,
          isLoading: false
        }),
        success: (prevState) => {
          return {
            ...prevState,
            layout: payload.data,
            error: null,
            loading: false,
            isLoading: false
          };
        }
      });

    case SAVE_LAYOUT_V2:
      return handleAction(state, action, {
        failure: (prevState) => ({
          ...prevState,
          error: payload?.error || 'Error al guardar el layout'
        }),
        success: (prevState) => ({
          ...prevState,
          layout: payload.data,
          error: null
        })
      });

    // ==================== Widget Management ====================

    case ADD_WIDGET_V2:
      return handleAction(state, action, {
        failure: (prevState) => ({
          ...prevState,
          error: payload?.error || 'Error al agregar widget'
        }),
        success: (prevState) => ({
          ...prevState,
          layout: {
            ...prevState.layout,
            widgets: [...(prevState.layout?.widgets || []), payload.data.widget]
          },
          error: null
        })
      });

    case UPDATE_WIDGET_POSITIONS_V2:
      return handleAction(state, action, {
        failure: (prevState) => ({
          ...prevState,
          error: payload?.error || 'Error al actualizar posiciones'
        }),
        success: (prevState) => {
          // Actualizar las posiciones de los widgets en el layout
          const updatedWidgets = action.meta?.widgets || [];
          
          if (!updatedWidgets.length) {
            return { ...prevState, error: null };
          }

          const widgetsMap = new Map(
            updatedWidgets.map(w => [w.widgetId, w])
          );

          const newWidgets = prevState.layout.widgets.map(widget => {
            const update = widgetsMap.get(widget.widgetId);
            if (update) {
              return {
                ...widget,
                x: update.x,
                y: update.y,
                w: update.w,
                h: update.h,
              };
            }
            return widget;
          });

          return {
            ...prevState,
            layout: {
              ...prevState.layout,
              widgets: newWidgets
            },
            error: null
          };
        }
      });

    case UPDATE_WIDGET_CONFIG_V2:
      return handleAction(state, action, {
        failure: (prevState) => ({
          ...prevState,
          error: payload?.error || 'Error al actualizar widget'
        }),
        success: (prevState) => ({
          ...prevState,
          layout: {
            ...prevState.layout,
            widgets: prevState.layout.widgets.map(widget =>
              widget.widgetId === action.meta.widgetId
                ? { ...widget, config: payload.data.config }
                : widget
            )
          },
          error: null
        })
      });

    case DELETE_WIDGET_V2:
      return handleAction(state, action, {
        failure: (prevState) => ({
          ...prevState,
          error: payload?.error || 'Error al eliminar widget'
        }),
        success: (prevState) => {
          const newWidgetData = { ...prevState.widgetData };
          delete newWidgetData[action.meta.widgetId];
          
          return {
            ...prevState,
            layout: {
              ...prevState.layout,
              widgets: prevState.layout.widgets.filter(
                widget => widget.widgetId !== action.meta.widgetId
              )
            },
            widgetData: newWidgetData,
            error: null
          };
        }
      });

    // ==================== Widget Data ====================

    case FETCH_WIDGET_DATA_V2:
      return handleAction(state, action, {
        start: (prevState) => ({
          ...prevState,
          widgetData: {
            ...prevState.widgetData,
            [action.meta.widgetId]: {
              ...prevState.widgetData[action.meta.widgetId],
              loading: true,
              error: null
            }
          }
        }),
        failure: (prevState) => ({
          ...prevState,
          widgetData: {
            ...prevState.widgetData,
            [action.meta.widgetId]: {
              ...prevState.widgetData[action.meta.widgetId],
              loading: false,
              error: payload?.error || 'Error al cargar datos del widget'
            }
          }
        }),
        success: (prevState) => ({
          ...prevState,
          widgetData: {
            ...prevState.widgetData,
            [action.meta.widgetId]: {
              data: payload.data,
              loading: false,
              error: null,
              lastUpdated: new Date().toISOString()
            }
          }
        })
      });

    case FETCH_BATCH_WIDGET_DATA_V2:
      return handleAction(state, action, {
        failure: (prevState) => ({
          ...prevState,
          error: payload?.error || 'Error al cargar datos en batch'
        }),
        success: (prevState) => {
          const newWidgetData = { ...prevState.widgetData };
          
          // Actualizar datos de cada widget
          Object.entries(payload.data).forEach(([widgetId, widgetInfo]) => {
            if (widgetInfo.error) {
              newWidgetData[widgetId] = {
                ...newWidgetData[widgetId],
                loading: false,
                error: widgetInfo.error
              };
            } else {
              newWidgetData[widgetId] = {
                data: widgetInfo.data,
                loading: false,
                error: null,
                lastUpdated: new Date().toISOString()
              };
            }
          });

          return {
            ...prevState,
            widgetData: newWidgetData,
            error: null
          };
        }
      });

    // ==================== UI State ====================

    case SET_DASHBOARD_V2_LOADING:
      return {
        ...state,
        loading: payload,
        isLoading: payload
      };

    case SET_DASHBOARD_V2_ERROR:
      return {
        ...state,
        error: payload,
        loading: false,
        isLoading: false
      };

    case LOGOUT:
      return initialState;

    default:
      return state;
  }
};

export default dashboardV2Reducer;
